import { featuredTech, additionalTech } from '@/mocks/playgroundData';
import { useInView } from '@/hooks/useInView';

function badgeClasses(style: string) {
  return style === 'primary'
    ? 'bg-primary-500 text-background-50'
    : 'bg-accent-500 text-background-50';
}

function FeaturedCard({ item, delay }: { item: typeof featuredTech[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`relative flex flex-col rounded-2xl bg-background-50 border border-background-200/70 p-6 transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <span
        className={`absolute -top-3 left-6 px-3 py-1 rounded-full text-[11px] font-semibold ${badgeClasses(
          item.badgeStyle
        )}`}
      >
        {item.badge}
      </span>

      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-background-100 text-primary-600 mb-5 mt-1">
        <i className={`${item.icon} text-2xl`} />
      </div>
      <h3 className="text-lg font-semibold text-foreground-950 mb-2">{item.title}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed flex-1 mb-5">{item.description}</p>
      <a
        href="#"
        className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap self-start"
      >
        {item.cta}
        <i className="ri-arrow-right-line" />
      </a>
    </div>
  );
}

function AdditionalCard({ item, delay }: { item: typeof additionalTech[0]; delay: number }) {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <div
      ref={ref}
      className={`flex flex-col sm:flex-row sm:items-center gap-5 rounded-2xl bg-background-50 border border-background-200/70 p-6 transition-all duration-700 ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-xl bg-background-100 text-accent-600">
        <i className={`${item.icon} text-2xl`} />
      </div>
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-foreground-950 mb-1">{item.title}</h3>
        <p className="text-sm text-foreground-600 leading-relaxed">{item.description}</p>
      </div>
      <a
        href="#"
        className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-secondary-500 text-background-50 text-sm font-medium rounded-full hover:bg-secondary-600 transition-colors whitespace-nowrap shrink-0"
      >
        {item.cta}
        <i className="ri-arrow-right-line" />
      </a>
    </div>
  );
}

export default function ExploreTech() {
  const [ref, inView] = useInView<HTMLDivElement>();

  return (
    <section id="explore" className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
      <div
        ref={ref}
        className={`max-w-3xl mb-12 transition-all duration-700 ${
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <h2
          className="text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-3"
          style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}
        >
          Explore the Technology
        </h2>
        <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
          Interactive demonstrations and examples of how bioMail and bioVerifier can help you.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {featuredTech.map((item, i) => (
          <FeaturedCard key={item.id} item={item} delay={i * 100} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {additionalTech.map((item, i) => (
          <AdditionalCard key={item.id} item={item} delay={i * 100} />
        ))}
      </div>
    </section>
  );
}